import { ref, computed, type Ref } from 'vue';
import type { P2PMessage, IPeerConnection, ConnectionType } from '@/types';
import { usePlayerStore, useGameConfigStore } from '@/stores';

// Type para PeerConnection importado dinámicamente
type PeerConnectionClass = {
	new(callbacks: {
		onConnected?: () => void;
		onMessage?: (message: P2PMessage) => void;
		onDisconnected?: () => void;
	}, connectionType?: ConnectionType): IPeerConnection;
};

// Callbacks específicos por jugador
type PlayerPeerCallbacks = {
	onMessage?: (message: P2PMessage, fromPlayerId: string) => void;
	onConnected?: (playerId: string) => void;
	onDisconnected?: (playerId: string) => void;
};

export function usePeer() {
	const playerStore = usePlayerStore();
	const gameConfigStore = useGameConfigStore();

	// Use a computed property that always reads from gameConfig
	// This ensures connection type is always in sync with game settings
	const connectionType = computed<ConnectionType>(() => {
		// Map 'local' → 'lan', 'internet' → 'internet'
		return gameConfigStore.options.connectionMode === 'internet' ? 'internet' : 'lan';
	});

	// Para el host: Map de conexiones a múltiples guests
	// Para guests: conexión única al host (usando una key especial 'host')
	const peerConnections: Ref<Map<string, IPeerConnection>> = ref(new Map());
	const peerInitialized: Ref<boolean> = ref(false);

	const generatePlayerId = (): string => {
		return 'player_' + Math.random().toString(36).substr(2, 9);
	};

	const generateRoomId = (): string => {
		// Generar 6 caracteres alfanuméricos (solo mayúsculas para fácil lectura)
		const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excluye I, 1, O, 0 por confusión visual
		let result = '';
		for (let i = 0; i < 6; i++) {
			result += chars.charAt(Math.floor(Math.random() * chars.length));
		}
		return result;
	};

	const generatePlayerName = (usedNames?: Set<string>): string => {
		let attempts = 0;
		const maxAttempts = 10;

		while (attempts < maxAttempts) {
			const useCelebrityName = Math.random() < 0.3;
			let generatedName = '';

			if (useCelebrityName) {
				const celebrityNames = [
					'Pedro Sánchez', 'Ábalos', 'Pablo Iglesias', "Santiago Abascal", "M. Rajoy"
				];
				generatedName = celebrityNames[Math.floor(Math.random() * celebrityNames.length)];
			} else {
				const adjectives = ['Feliz', 'Rápido', 'Valiente', 'Astuto', 'Alegre', 'Fuerte', 'Inquieto', 'Tranquilo', 'Curioso', 'Audaz', 'Juguetón', 'Sereno', 'Vigoroso', 'Divertido', 'Brillante', 'Cariñoso', 'Osado', 'Radiante', 'Saltarín', 'Energético'];
				const nouns = ['Tigre', 'Águila', 'León', 'Toro', 'Lobo', 'Halcón', 'Gato', 'Perro', 'Caballo', 'Elefante', 'Jirafa', 'Zorro', 'Oso', 'Conejo', 'Ciervo', 'Mapache', 'Búho', 'Delfín', 'Tortuga', 'Mariposa'];
				const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
				const noun = nouns[Math.floor(Math.random() * nouns.length)];
				generatedName = `${adj} ${noun}`;
			}

			// Si no se proporcionan nombres usados o el nombre es único, retornarlo
			if (!usedNames || !usedNames.has(generatedName)) {
				return generatedName;
			}

			attempts++;
		}

		// Si después de maxAttempts no encontramos nombre único, retornar uno con sufijo
		const fallbackName = usedNames ? `Jugador ${Date.now().toString().slice(-4)}` : 'Jugador Anónimo';
		return fallbackName;
	};

	// Inicializar peer del host (para crear la sala)
	const initializePeer = async (
		onConnected?: () => void,
		onMessage?: (message: P2PMessage) => void,
		onDisconnected?: () => void
	): Promise<void> => {
		console.log('[usePeer] initializePeer - starting, existing peers:', peerConnections.value.size, 'connection type:', connectionType.value);
		const { PeerConnection } = await import('../lib/peer.ts') as { PeerConnection: PeerConnectionClass; };

		console.log('[usePeer] Creating new PeerConnection instance for host');
		const hostPeer = new PeerConnection({
			onConnected: () => {
				playerStore.setConnected(true);
				onConnected?.();
			},
			onMessage: (message: P2PMessage) => {
				onMessage?.(message);
			},
			onDisconnected: () => {
				playerStore.setConnected(false);
				peerInitialized.value = false;
				onDisconnected?.();
			}
		}, connectionType.value); // Pasar tipo de conexión

		// Guardar el peer del host con key especial 'host'
		peerConnections.value.set('host', hostPeer);
		peerInitialized.value = true;
	};

	// Crear offer desde el host
	const createOffer = async (): Promise<string> => {
		console.log('[usePeer] createOffer called, peers:', peerConnections.value.size, 'initialized:', peerInitialized.value);
		const hostPeer = peerConnections.value.get('host');
		if (!hostPeer || !peerInitialized.value) {
			throw new Error('Peer no inicializado. Espera a que se inicialice la conexión.');
		}
		const rawOfferCode = await hostPeer.createOffer();

		// Embed metadata in the offer for server-mediated signaling
		const offerData = {
			roomId: playerStore.roomId,          // ID único de la sala (separado del player ID)
			slotId: 'guest_slot_0',              // First guest slot
			sdp: rawOfferCode,                   // Datos WebRTC SDP reales
			timestamp: Date.now()
		};

		// Codificar como Base64 para el QR
		const offerCode = btoa(JSON.stringify(offerData));

		console.log('[usePeer] createOffer - offer created with roomId:', playerStore.roomId);
		return offerCode;
	};

	// Crear answer desde un guest
	const createAnswer = async (
		offer: string,
		onMessage?: (message: P2PMessage) => void,
		onHostDisconnected?: () => void
	): Promise<string> => {
		console.log('[usePeer] createAnswer called, peers:', peerConnections.value.size, 'initialized:', peerInitialized.value);

		// Procesar offer para extraer metadatos si existen
		let actualOfferSDP = offer;
		let extractedSlotId: string | undefined;

		try {
			// Intentar parsear como JSON con metadatos
			const offerData = JSON.parse(atob(offer));

			if (offerData.sdp && offerData.slotId && offerData.roomId) {
				// Es el nuevo formato con metadatos
				actualOfferSDP = offerData.sdp;
				extractedSlotId = offerData.slotId;
				console.log('[usePeer] Offer con metadatos - slotId:', extractedSlotId, 'roomId:', offerData.roomId);
			} else {
				// Es formato antiguo (solo SDP)
				actualOfferSDP = offer;
			}
		} catch (e) {
			// No es JSON válido, asume formato antiguo
			actualOfferSDP = offer;
		}

		// Para guests, creamos una nueva conexión al host
		const { PeerConnection } = await import('../lib/peer.ts') as { PeerConnection: PeerConnectionClass; };

		const guestPeer = new PeerConnection({
			onConnected: () => {
				playerStore.setConnected(true);
			},
			onMessage: (message: P2PMessage) => {
				// Los guests reciben mensajes del host
				console.log('[usePeer] Guest received message from host:', message.type);
				// Forward to the app's message handler
				onMessage?.(message);
			},
			onDisconnected: () => {
				console.log('[usePeer] Guest detected host disconnection');
				playerStore.setConnected(false);
				peerInitialized.value = false;
				// Execute custom callback if provided
				if (onHostDisconnected) {
					console.log('[usePeer] Executing host disconnection callback');
					onHostDisconnected();
				}
			}
		}, connectionType.value); // Pasar tipo de conexión

		// Guardar con key 'host' porque el guest solo se conecta al host
		peerConnections.value.set('host', guestPeer);
		peerInitialized.value = true;

		const rawAnswer = await guestPeer.createAnswer(actualOfferSDP);

		// Si el offer tenía slotId, incrustarlo en el answer
		if (extractedSlotId) {
			const answerData = {
				slotId: extractedSlotId,
				playerId: playerStore.myPlayerId, // Include guest's player ID
				sdp: rawAnswer,
				timestamp: Date.now()
			};
			return btoa(JSON.stringify(answerData));
		}

		return rawAnswer;
	};

	// HOST: Crear oferta para un nuevo guest (crea una NUEVA conexión)
	const createOfferForGuest = async (
		guestPlayerId: string,
		callbacks?: PlayerPeerCallbacks
	): Promise<string> => {
		console.log('[usePeer] createOfferForGuest - creating offer for guest:', guestPlayerId);

		// Crear NUEVA PeerConnection para este guest específico
		const { PeerConnection } = await import('../lib/peer.ts') as { PeerConnection: PeerConnectionClass; };

		const newGuestPeer = new PeerConnection({
			onConnected: () => {
				playerStore.updateConnectionStatus(guestPlayerId, 'connected');
				callbacks?.onConnected?.(guestPlayerId);
			},
			onMessage: (message: P2PMessage) => {
				// Identificar de qué jugador viene el mensaje
				callbacks?.onMessage?.(message, guestPlayerId);
			},
			onDisconnected: () => {
				playerStore.updateConnectionStatus(guestPlayerId, 'disconnected');
				peerConnections.value.delete(guestPlayerId);
				callbacks?.onDisconnected?.(guestPlayerId);
			}
		}, connectionType.value); // Pasar tipo de conexión

		// Guardar esta conexión específica del guest
		peerConnections.value.set(guestPlayerId, newGuestPeer);

		// Crear oferta para ESTA conexión específica
		const rawOfferCode = await newGuestPeer.createOffer();

		// Incrustar metadatos en el offer
		const offerData = {
			roomId: playerStore.roomId,          // ID único de la sala (separado del player ID)
			slotId: guestPlayerId,                // guest_slot_0, guest_slot_1, etc.
			sdp: rawOfferCode,                   // Datos WebRTC SDP reales
			timestamp: Date.now()
		};

		// Codificar como Base64 para el QR
		const offerCode = btoa(JSON.stringify(offerData));

		console.log('[usePeer] createOfferForGuest - offer created for guest:', guestPlayerId);
		return offerCode;
	};

	// HOST: Aceptar el answer de un guest en una conexión específica
	const acceptGuestAnswer = async (
		guestPlayerId: string,
		answerCode: string
	): Promise<void> => {
		console.log('[usePeer] acceptGuestAnswer - accepting answer for guest:', guestPlayerId);
		const guestPeer = peerConnections.value.get(guestPlayerId);
		if (!guestPeer) {
			throw new Error(`No peer connection found for guest: ${guestPlayerId}`);
		}
		await guestPeer.acceptAnswer(answerCode);
		console.log('[usePeer] acceptGuestAnswer - connection established for guest:', guestPlayerId);
	};

	// Enviar mensaje a un peer específico
	const sendTo = (playerId: string, message: P2PMessage): void => {
		const peer = peerConnections.value.get(playerId);
		if (!peer) {
			console.error('[usePeer] sendTo - no peer found for:', playerId);
			return;
		}
		peer.send(message);
	};

	// Broadcast a todos los peers (solo usado por el host)
	const broadcast = (message: P2PMessage, excludePlayerId?: string): void => {
		peerConnections.value.forEach((peer, playerId) => {
			if (playerId !== excludePlayerId) {
				peer.send(message);
			}
		});
	};

	// Cerrar conexión de un guest específico
	const closeGuestConnection = (guestSlotId: string): void => {
		console.log('[usePeer] closeGuestConnection - closing connection for:', guestSlotId);
		const guestPeer = peerConnections.value.get(guestSlotId);
		if (guestPeer) {
			guestPeer.close();
			peerConnections.value.delete(guestSlotId);
			console.log('[usePeer] closeGuestConnection - connection closed and removed for:', guestSlotId);
		} else {
			console.warn('[usePeer] closeGuestConnection - no connection found for:', guestSlotId);
		}
	};

	// Guests envían mensajes solo al host (arquitectura star topology)
	const send = (message: P2PMessage): void => {
		const hostPeer = peerConnections.value.get('host');
		if (!hostPeer || !peerInitialized.value) {
			throw new Error('Peer no inicializado.');
		}
		hostPeer.send(message);
	};

	// Cambiar tipo de conexión
	const setConnectionType = (type: ConnectionType): void => {
		console.log('[usePeer] Cambiando tipo de conexión:', connectionType.value, '→', type);

		// Update gameConfig instead of localStorage directly
		// The computed connectionType will automatically reflect the change
		const newMode: 'internet' | 'local' = type === 'internet' ? 'internet' : 'local';
		gameConfigStore.updateOptions({ connectionMode: newMode });

		// Limpiar conexiones existentes al cambiar tipo
		if (peerConnections.value.size > 0) {
			console.log('[usePeer] Limpiando conexiones existentes al cambiar tipo');
			peerConnections.value.forEach((conn, key) => {
				conn.close();
				peerConnections.value.delete(key);
			});
			peerInitialized.value = false;
		}
	};

	return {
		peerConnections,
		peerInitialized,
		myPlayerId: playerStore.myPlayerId,
		isHost: playerStore.isHost,
		players: playerStore.players,
		offerCode: playerStore.offerCode,
		connectionType,
		generatePlayerId,
		generateRoomId,
		generatePlayerName,
		initializePeer,
		setConnectionType,
		createOffer,
		createAnswer,
		createOfferForGuest,
		acceptGuestAnswer,
		send,
		sendTo,
		broadcast,
		closeGuestConnection
	};
}
